import translate from 'core/translate'
import React from 'react'
import InputMask from 'react-input-mask'

const sizerStyle: { [index: string]: string | number } = {
    position: 'absolute',
    top: 0,
    left: 0,
    visibility: 'hidden',
    height: 0,
    overflow: 'scroll',
    whiteSpace: 'pre',
}

export type DropdownSearchableValue = string | number

export interface IDropdownSearchableProps {
    id?: string
    className: string // className for the outer element
    defaultValue?: DropdownSearchableValue // default field value
    inputClassName: string // className for the input element
    inputStyle?: React.CSSProperties // css styles for the input element
    minWidth: number | string
    onAutosize?: (newWidth: number | string) => void // onAutosize handler: function(newWidth) {}
    onChange: React.ChangeEventHandler<HTMLInputElement> // onChange handler: function(newValue) {}
    placeholder?: string // placeholder text
    placeholderIsMinWidth?: boolean // don't collapse size to less than the placeholder
    style?: React.CSSProperties | null // css styles for the outer element
    value: DropdownSearchableValue // field value
    wrapperWidth?: boolean
    mask?: string
    placeholderText?: string | null
    disabled?: boolean
    onFocus?: React.FocusEventHandler<HTMLInputElement>
    onBlur?: React.FocusEventHandler<HTMLInputElement>
    type?: string
    disablePlaceholder?: boolean
}

interface IState {
    inputWidth: string | number
}

class DropdownSearchable extends React.Component<IDropdownSearchableProps> {
    static defaultProps: { minWidth: number }
    mounted: boolean = false
    state: IState
    sizer: HTMLDivElement | null = null
    placeHolderSizer: HTMLDivElement | null = null
    input: HTMLInputElement | null = null

    constructor(p_: IDropdownSearchableProps) {
        super(p_)
        this.state = {
            inputWidth: p_.minWidth,
        }

        this.inputRef = this.inputRef.bind(this)
        this.sizerRef = this.sizerRef.bind(this)
    }
    componentDidMount() {
        this.mounted = true
        this.copyInputStyles()
    }
    componentDidUpdate(_prevProps: IDropdownSearchableProps, prevState: IState) {
        if (prevState.inputWidth !== this.state.inputWidth) {
            if (typeof this.props.onAutosize === 'function') {
                this.props.onAutosize(this.state.inputWidth)
            }
        }
        this.updateInputWidth()
    }
    componentWillUnmount() {
        this.mounted = false
    }
    inputRef(el: HTMLInputElement | null) {
        this.input = el
    }
    placeHolderSizerRef(el: HTMLDivElement | null) {
        this.placeHolderSizer = el
    }
    sizerRef(el: HTMLDivElement | null) {
        this.sizer = el
    }
    copyInputStyles() {
        if (this.mounted || !window.getComputedStyle) {
            return
        }
        const inputStyle = this.input && window.getComputedStyle(this.input)
        if (!inputStyle) {
            return
        }
        const widthNode = this.sizer
        if (!widthNode) {
            return
        }
        widthNode.style.fontSize = inputStyle.fontSize
        widthNode.style.fontFamily = inputStyle.fontFamily
        widthNode.style.fontWeight = inputStyle.fontWeight
        widthNode.style.fontStyle = inputStyle.fontStyle
        widthNode.style.letterSpacing = inputStyle.letterSpacing
        widthNode.style.textTransform = inputStyle.textTransform
        if (this.props.placeholder) {
            const placeholderNode = this.placeHolderSizer
            if (!placeholderNode) {
                return
            }
            placeholderNode.style.fontSize = inputStyle.fontSize
            placeholderNode.style.fontFamily = inputStyle.fontFamily
            placeholderNode.style.fontWeight = inputStyle.fontWeight
            placeholderNode.style.fontStyle = inputStyle.fontStyle
            placeholderNode.style.letterSpacing = inputStyle.letterSpacing
            placeholderNode.style.textTransform = inputStyle.textTransform
        }
    }
    updateInputWidth() {
        if (!this.mounted || !this.sizer || typeof this.sizer.scrollWidth === 'undefined') {
            return
        }
        let newInputWidth
        if (
            this.props.placeholder &&
            this.placeHolderSizer &&
            (!this.props.value || (this.props.value && this.props.placeholderIsMinWidth))
        ) {
            newInputWidth = Math.max(this.sizer.scrollWidth, this.placeHolderSizer.scrollWidth) + 2
        } else {
            newInputWidth = this.sizer.scrollWidth + 2
        }
        if (newInputWidth < this.props.minWidth) {
            newInputWidth = this.props.minWidth
        }
        if (newInputWidth !== this.state.inputWidth) {
            this.setState({
                inputWidth: newInputWidth,
            })
        }
    }
    getInput() {
        return this.input
    }
    focus() {
        this.input?.focus()
    }
    blur() {
        this.input?.blur()
    }
    select() {
        this.input?.select()
    }
    render() {
        const p_ = this.props
        const sizerValue = this.props.defaultValue ?? this.props.value ?? ''

        const wrapperStyle: React.CSSProperties = { ...(this.props.style || {}) }
        if (!Object.keys(wrapperStyle).length) wrapperStyle.display = 'inline-block'
        const inputStyle: React.CSSProperties = {
            ...this.props.inputStyle,
            width: '100%',
            boxSizing: 'content-box',
        }
        const inputProps: React.InputHTMLAttributes<HTMLInputElement> = {
            id: p_.id,
            className: p_.inputClassName,
            defaultValue: p_.defaultValue,
            disabled: p_.disabled,
            onBlur: p_.onBlur,
            onChange: p_.onChange,
            onFocus: p_.onFocus,
            placeholder: p_.placeholderText ? p_.placeholderText : !p_.disablePlaceholder ? translate('search') : '',
            style: inputStyle,
            type: p_.type,
            value: p_.value,
        }
        return (
            <div className={this.props.className} style={wrapperStyle}>
                {!p_.mask && <input {...inputProps} ref={this.inputRef} autoComplete={'off'} />}
                {p_.mask && <InputMask mask={p_.mask} {...inputProps} inputRef={this.inputRef} autoComplete={'off'} />}
                <div ref={this.sizerRef} style={sizerStyle}>
                    {sizerValue}
                </div>
            </div>
        )
    }
}

DropdownSearchable.defaultProps = {
    minWidth: 1,
}

export default DropdownSearchable
